/**
 * Dates and overdueness, on the client.
 *
 * Both of these are deliberately NOT in a Convex query. A query is cached
 * until its data changes, and time passing is not a data change -- a count
 * computed server-side would be correct when it was written and wrong at
 * midnight, staying wrong until something unrelated wrote to the table. The
 * rows carry `dueDay`; the comparison happens here, where the clock is always
 * right. The same note is in `personActivities.viewerSummary`.
 */

/**
 * Today, as a "YYYY-MM-DD" Vienna day -- the same definition the backend uses
 * (convex/days.ts). Not `toISOString()`, which is UTC and would call a
 * deadline "not yet passed" for the hour after Vienna midnight.
 */
export function viennaToday(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Vienna",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** The shape the test needs, so anything with these fields can be asked. */
export type OverdueCandidate = {
  required: boolean;
  dueDay: string | null;
  status: string;
};

/**
 * Late, and somebody should do something about it.
 *
 * `required` comes first because encouraged work never goes overdue (model
 * §7) -- it is real work that simply never counts as late, and a dashboard
 * that chased people about an optional patent application would be worse than
 * one that said nothing. The four excluded statuses are the ones where a date
 * in the past means nothing: finished, filed away, called off, or stopped.
 */
export function isActivityOverdue(row: OverdueCandidate, today = viennaToday()): boolean {
  return row.required
    && row.dueDay !== null
    && row.dueDay < today
    && row.status !== "completed"
    && row.status !== "archived"
    && row.status !== "cancelled"
    && row.status !== "paused";
}
